import type { Guide } from "../types";

// ── Content ──────────────────────────────────────────────────────────────────
// How to create the things you put on screens: uploading media, adding
// subtitles to videos, building playlists, designing multi-zone channels, and
// designing from scratch in the Canvas editor.

export const contentGuides: Guide[] = [
  {
    slug: "upload-media",
    category: "content",
    route: "/content/upload-media",
    title: "Upload media",
    summary:
      "Add images, videos, audio and documents to your Media Library and organise them into folders.",
    intro:
      "Your Media Library is where every file lives before it goes on a screen. Anything you upload here can be put straight onto a screen, added to a playlist, or placed inside a channel zone. This guide covers uploading files, keeping them tidy with folders, checking a file’s details, adding several files to playlists at once, and deleting files safely.",
    prerequisites: [
      "You are signed in to the dashboard with permission to manage content.",
      "You have the files ready on your computer.",
    ],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-10-11",
    steps: [
      {
        title: "Open the Media Library",
        blocks: [
          {
            type: "p",
            text: "In the left sidebar, click Media. The Media Library opens with your folders on the left and your files on the right. Use Search Media and the All types filter (Image or Video) at the top to find files quickly.",
          },
          {
            type: "p",
            text: "The storage bar under the header shows how much of your storage you’ve used and the Max file size your plan allows.",
          },
        ],
        screenshot: {
          src: "content/upload-media/01.png",
          alt: "The Media Library with the sidebar Media item highlighted",
          caption: "The Media Library — folders on the left, files on the right.",
          todo: "Media Library page (/media): header 'Media Library' with subtitle 'Manage your media files, subtitles and translations', Search Media, All types, Upload button, storage bar, Folders card and file table. Sidebar 'Media' highlighted.",
        },
      },
      {
        title: "Click Upload and pick your files",
        blocks: [
          {
            type: "p",
            text: "First click the folder you want the files to go into (or Library for the top level). Then click Upload and choose one or more files. You can also drag files from your desktop onto the file list — you’ll see “Drop files to upload” and the folder they’ll land in.",
          },
          {
            type: "p",
            text: "A progress bar shows each file as it uploads, then “Processing…” while it’s prepared. Large videos are sent in parts automatically, so big files upload reliably — you don’t need to do anything special.",
          },
          {
            type: "ul",
            items: [
              "Images — JPG, PNG, GIF, WebP, SVG.",
              "Video — MP4, WebM, MOV, AVI, MPG.",
              "Audio — MP3, M4A, AAC, OGG, WAV (used for channel background music).",
              "Documents — PDF and PowerPoint (PPT, PPTX); each page plays like a slide.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "If you’re viewing all spaces at once, PulsePanels first asks “Where do you want to upload?” so the files land in the right space. After a video uploads, lighter copies are prepared in the background for previews and screens; your original file is never changed, and Download always gives you the original.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Videos bigger than Full HD or faster than 30 frames per second — for example 4K, or 50 or 60 fps — also get a screen copy scaled to fit 1920 × 1080 (1080 × 1920 for portrait) at up to 30 fps, which screens play instead. Many players can’t play the original smoothly. You don’t need to do anything: screens switch to the copy by themselves once it’s ready.",
          },
        ],
        screenshot: {
          src: "content/upload-media/02.png",
          alt: "Files being uploaded to the Media Library with a progress bar",
          caption: "Pick files or drag them onto the list.",
          todo: "Media Library with an upload in progress: the 'Uploading…' bar showing 'Uploading 1 of 2: <file>' and a percentage. Alternative: the 'Drop files to upload' overlay while dragging a file over the list.",
        },
      },
      {
        title: "Organise into folders (optional)",
        blocks: [
          {
            type: "p",
            text: "Click the new-folder icon next to Folders. In the New folder dialog, type a Folder name and click Create folder. The number next to each folder shows how many files it holds.",
          },
          {
            type: "ul",
            items: [
              "Move a file: drag its row onto a folder, or open its ⋮ menu and choose Move to.",
              "Move several files: tick their checkboxes, then click Move selected.",
              "Rename, move or delete a folder from the ⋮ next to it. A folder must be empty before you can delete it.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Folders are just for organisation. A file can be used on a screen no matter which folder it lives in.",
          },
        ],
        screenshot: {
          src: "content/upload-media/03.png",
          alt: "The New folder dialog in the Media Library",
          caption: "Folders help you find files fast as your library grows.",
          todo: "Media Library with the 'New folder' dialog open (Parent: Library, Folder name field, Cancel / Create folder).",
        },
      },
      {
        title: "Check a file’s details",
        blocks: [
          {
            type: "p",
            text: "Click any file to open its details panel under the list. The Details tab shows the file name, type, resolution, duration, size and upload date. Videos also get a Subtitles tab, and Activity shows when the file was uploaded and last changed.",
          },
          {
            type: "p",
            text: "The ⋮ menu on each row has everything else: Preview, Download, Add to Playlists, Rename, Tags, Move to and Delete from library. Images and documents also offer Open with Canvas, and videos offer Generate subtitles.",
          },
        ],
        screenshot: {
          src: "content/upload-media/04.png",
          alt: "The details panel for a selected video, with Details, Subtitles and Activity tabs",
          caption: "Click any file to see its details.",
          todo: "A generic sample video selected in the Media Library (no real people or brands), details panel open on the Details tab showing File name, File type, Resolution, Duration.",
        },
      },
      {
        title: "Add several files to playlists at once",
        blocks: [
          {
            type: "p",
            text: "Tick the checkbox next to each file you want, or tick the box in the header to select every file on the page. A bar appears above the list showing how many are selected; click Add to Playlists.",
          },
          {
            type: "note",
            tone: "tip",
            text: "You can only select files on the page you’re on. For a big batch, set the page size at the bottom of the list to 50 or 100 per page first.",
          },
        ],
        screenshot: {
          src: "content/upload-media/05.png",
          alt: "Three files ticked in the Media Library, with the selection bar showing Add to Playlists, Move selected and Delete selected",
          caption: "Tick the files, then click Add to Playlists in the selection bar.",
          todo: "Media Library, Promotions folder, three image rows ticked; the bar above the list reads '3 selected' with Clear, Add to Playlists, Move selected and Delete selected.",
        },
      },
      {
        title: "Choose the playlists",
        blocks: [
          {
            type: "p",
            text: "In the Add to playlist dialog, tick one or more playlists and click Add to playlist. Every selected file is added to the end of each playlist you ticked.",
          },
          {
            type: "ul",
            items: [
              "Files are added in the order you uploaded them, oldest first — so slides uploaded as 1, 2, 3 … play in that order, even though the Media Library lists the newest first.",
              "Images play for 5 seconds and videos for their full length. Change durations or the order in the playlist editor afterwards.",
              "To add a single file, use Add to Playlists in that file’s ⋮ menu instead.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Screens keep showing the playlist as it was last applied. When you’re ready for them to show the new files, click Apply changes at the right end of the page’s title row — the number on it counts the screens waiting.",
          },
        ],
        screenshot: {
          src: "content/upload-media/06.png",
          alt: "The Add to playlist dialog listing playlists, with one ticked",
          caption: "Tick one or more playlists, then click Add to playlist.",
          todo: "The 'Add to playlist' dialog opened from the selection bar: subtitle 'Choose one or more playlists to add these 3 files, in upload order.', playlist list with one ticked, Cancel / Add to playlist buttons.",
        },
      },
      {
        title: "Delete files safely",
        blocks: [
          {
            type: "p",
            text: "Choose Delete from library in a file’s ⋮ menu, or tick several files and click Delete selected. Before anything is removed, the dialog checks where each file is used:",
          },
          {
            type: "ul",
            items: [
              "Assigned directly to screens — unassign it from those screens first.",
              "Used in channels — each channel is listed with an Open button; remove the file from the channel first.",
              "Currently used by playlists — the file is also removed from those playlists when you delete it.",
              "Also used in apps or emergency templates — listed for your information, with an Open button for each.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Deleting a file can’t be undone. Check the usage list in the dialog before you confirm.",
          },
        ],
      },
    ],
    related: [
      { label: "Add subtitles to a video", to: "/content/add-subtitles" },
      { label: "Build a playlist", to: "/content/build-a-playlist" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: [
      "media",
      "upload",
      "image",
      "video",
      "audio",
      "pdf",
      "powerpoint",
      "folder",
      "storage",
      "large file",
      "delete",
      "add to playlist",
      "select multiple",
      "bulk",
    ],
  },

  {
    slug: "add-subtitles",
    category: "content",
    route: "/content/add-subtitles",
    title: "Add subtitles to a video",
    summary:
      "Generate captions for a video automatically, translate them, edit the timing, and play a subtitled copy on your screens.",
    intro:
      "PulsePanels can listen to a video, write its subtitles for you, and translate them into another language. You review and edit the result, then publish it. Publishing also saves a subtitled copy of the video to your Media Library, with the captions built into the picture, so it shows subtitles on every screen.",
    prerequisites: [
      "The video is already in your Media Library.",
      "You are signed in with permission to manage content.",
    ],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open the subtitle editor",
        blocks: [
          {
            type: "p",
            text: "Go to Media, open the ⋮ menu on a video and choose Generate subtitles. (Manage subtitles opens a video’s existing subtitles instead.) You can also click the video and use Generate / translate in its details panel.",
          },
          {
            type: "note",
            tone: "info",
            text: "Subtitles are available for videos only — not for images, audio or documents.",
          },
        ],
        screenshot: {
          src: "content/add-subtitles/01.png",
          alt: "A video's ⋮ menu in the Media Library with Generate subtitles highlighted",
          caption: "Start from any video in your library.",
          todo: "Media Library, ⋮ menu open on a generic sample video showing Generate subtitles (NEW tag), Manage subtitles, Export with subtitles.",
        },
      },
      {
        title: "Choose the languages and generate",
        blocks: [
          {
            type: "p",
            text: "At the top of the Generate / Edit Subtitles page, set:",
          },
          {
            type: "ul",
            items: [
              "Spoken language — the language people speak in the video, or Auto detect.",
              "Subtitle language — Same as spoken language, or another language to translate into.",
              "Translate when required — leave this ticked so the subtitles are translated when the two languages differ.",
            ],
          },
          {
            type: "p",
            text: "Click Generate Subtitles. A progress bar shows each stage — for example Extracting audio, Transcribing audio and Translating — with a live percentage. It runs on the server, so you can leave the page and come back; click Cancel to stop it.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Languages available: English, French, Arabic, Spanish, German, Hindi, Urdu, Turkish, Russian and Chinese. Arabic and Urdu subtitles are shown right-to-left.",
          },
        ],
        screenshot: {
          src: "content/add-subtitles/02.png",
          alt: "The Generate / Edit Subtitles page with language pickers and a progress bar",
          caption: "Pick the languages, then generate.",
          todo: "Generate / Edit Subtitles page for a sample video: Spoken language, Subtitle language, Translate when required, the Generate Subtitles button (reads Generating… while a job runs), and the progress bar with a stage name and percentage. Current shot: a real job at Queued · 0% (no subtitle worker in the local demo); a mid-run stage such as Transcribing audio would be better.",
        },
      },
      {
        title: "Review and edit the cues",
        blocks: [
          {
            type: "p",
            text: "When it finishes, the subtitle cues appear in a list beside the video. Click a cue to edit its Start time, End time and Text, and choose its Position on screen (for example Bottom center). Play the video to check the timing — the current cue is shown over the picture.",
          },
          {
            type: "ul",
            items: [
              "Split cuts the selected cue at the playhead; Merge joins it with the next cue.",
              "Shift left −0.5s / Shift right +0.5s nudges a cue’s timing.",
              "Add cue creates a new one; Actions → Shift all moves every cue at once.",
              "Actions → Export .vtt or Export .srt downloads the subtitles as a file.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Keyboard shortcuts: ↑ / ↓ move between cues, Space plays or pauses, Ctrl+Z undoes and Ctrl+Y redoes.",
          },
        ],
        screenshot: {
          src: "content/add-subtitles/03.png",
          alt: "The subtitle editor with a cue selected and its start time, end time and text fields",
          caption: "Fix wording and timing cue by cue.",
          todo: "Subtitle editor with a cue selected: video with the subtitle overlay, Start time / End time / Text / Position fields, Split / Merge / Delete buttons, and the cue list on the right.",
        },
      },
      {
        title: "Save a draft or publish",
        blocks: [
          {
            type: "p",
            text: "Click Save draft to keep your edits without publishing. When you’re happy, click Save & Publish. This publishes the subtitles and also saves a subtitled copy of the video — named like “Lobby Video (subtitled).mp4” — in the same folder, with the captions built into the picture.",
          },
          {
            type: "p",
            text: "Put that subtitled copy on your screens, in a playlist, or in a channel zone, just like any other video. Publishing the same subtitles again updates the copy rather than making another one.",
          },
          {
            type: "note",
            tone: "info",
            text: "You can also choose Export with subtitles from the video’s ⋮ menu to make a subtitled copy from its default subtitle track.",
          },
        ],
        screenshot: {
          src: "content/add-subtitles/04.png",
          alt: "The subtitle editor with an edited cue and the Save draft and Save & Publish buttons",
          caption: "Save a draft, or publish when you’re happy.",
          todo: "Current shot: subtitle editor with an unsaved cue edit and the Save draft / Save & Publish buttons (bottom right). Better, once a subtitle worker is available: Media Library showing a sample video and its '(subtitled).mp4' copy, with the copy selected and the 'Subtitled' card in its details panel.",
        },
      },
      {
        title: "Translate, regenerate and manage tracks",
        blocks: [
          {
            type: "p",
            text: "Click the video and open its Subtitles tab. Each language is a track, showing whether it was Auto-generated, Translated, Imported or Manual, its number of cues, and its status (for example Ready for review or Published). Use the buttons on a track to Edit it, Set as default, Publish it, or Delete it.",
          },
          {
            type: "ul",
            items: [
              "Translate: open a track, pick a different Subtitle language and click Translate to <language>. A new translated track is created.",
              "Regenerate: open a track and click Regenerate Subtitles (or Re-translate for a translated track). The track’s cues are replaced — no duplicate track is added — and it goes back to review, so publish it again when you’re done.",
              "A job that’s still running shows in the Subtitles tab with Open and Cancel; a failed job has Retry.",
            ],
          },
          {
            type: "note",
            tone: "warning",
            text: "Deleting a track can’t be undone. The confirmation names the language and number of cues so you delete the right one. Screens aren’t affected: they never play subtitle tracks, and any “(subtitled)” copy made with Save & Publish is a separate video that keeps playing.",
          },
        ],
        screenshot: {
          src: "content/add-subtitles/05.png",
          alt: "The Subtitles tab of a video listing its subtitle tracks",
          caption: "Every language is a track you can edit, publish or delete.",
          todo: "Details panel of a sample video on the Subtitles tab, listing two tracks (e.g. English DEFAULT, Auto-generated, Published; French, Translated, Ready for review) with the Edit / Set as default / Publish / Delete buttons and 'Generate / translate'.",
        },
      },
    ],
    related: [
      { label: "Upload media", to: "/content/upload-media" },
      { label: "Build a playlist", to: "/content/build-a-playlist" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: [
      "subtitles",
      "captions",
      "closed captions",
      "transcribe",
      "transcription",
      "translate",
      "translation",
      "language",
      "srt",
      "vtt",
      "burn in",
    ],
  },

  {
    slug: "build-a-playlist",
    category: "content",
    route: "/content/build-a-playlist",
    title: "Build a playlist",
    summary:
      "Create an ordered, looping sequence of media, Canvas designs and apps to play on a screen.",
    intro:
      "A playlist is an ordered list of items that plays one after another and loops back to the start. Each item can be a media file (image, video or document), a Canvas design, or an app, and each has its own duration. Playlists are the most common way to run a rotating set of content on a screen.",
    prerequisites: [
      "You have some media in your library, or an app or Canvas design you want to include.",
      "You are signed in with permission to manage content.",
    ],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-10-22",
    steps: [
      {
        title: "Open Playlists",
        blocks: [
          {
            type: "p",
            text: "Click Playlists in the sidebar. The summary cards show how many playlists you have, and each playlist card has Edit, Rename and Duplicate buttons, plus icons for Download to PC and Delete. Use Search Playlists and the orientation filter to find one quickly.",
          },
        ],
        screenshot: {
          src: "content/build-a-playlist/01.png",
          alt: "The Playlists page with the sidebar Playlists item highlighted",
          caption: "The Playlists page — your list of rotating sequences.",
          todo: "Playlists page (/playlists) with generic playlist names (e.g. 'Lobby Loop', 'Lunch Specials'), summary cards, and the '+ New Playlist' button.",
        },
      },
      {
        title: "Create and name a new playlist",
        blocks: [
          {
            type: "p",
            text: "Click + New Playlist. In the Create playlist panel, enter a Playlist name — for example “Lobby Loop” or “Lunch Specials” — choose the Orientation (Landscape or Portrait) and the Target space, then click Create playlist.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Match the orientation to the screens the playlist will play on, so content isn’t cropped or letterboxed.",
          },
        ],
        screenshot: {
          src: "content/build-a-playlist/02.png",
          alt: "The Create playlist panel with name, orientation and target space fields",
          todo: "Create playlist side panel with 'Lunch Specials' typed as the Playlist name, Orientation Landscape and Target space selected.",
        },
      },
      {
        title: "Add content",
        blocks: [
          {
            type: "p",
            text: "In the playlist editor, click + Add Content. The Media Picker has three tabs:",
          },
          {
            type: "ul",
            items: [
              "Media — images, videos and documents from your library (you can also upload new files here).",
              "Canvas — designs you made in Canvas.",
              "Apps — live apps such as Weather, Clock or a Menu Board.",
            ],
          },
          {
            type: "p",
            text: "Tick what you want and click Add. You can also drag files from your computer onto the “Drag & drop media here” area under the list to upload and add them in one go.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Working in the Media Library already? Tick the files there and click Add to Playlists in the selection bar — they’re added in upload order, to as many playlists as you like.",
          },
          { type: "steps-ref", label: "See: Add several files to playlists at once", to: "/content/upload-media" },
        ],
        screenshot: {
          src: "content/build-a-playlist/03.png",
          alt: "The Media Picker open over the playlist editor, with Media, Canvas and Apps tabs",
          caption: "Add media, Canvas designs and apps.",
          todo: "Playlist editor with the Media Picker open: Media / Canvas / Apps tabs on the left, a few items ticked, and the Add button. Use a generic playlist name.",
        },
      },
      {
        title: "Set durations and reorder",
        blocks: [
          {
            type: "p",
            text: "Each row has a Duration in hours:minutes:seconds (for example 00:00:10). Images default to 5 seconds. Videos always play their full length, so their duration can’t be changed. For a document, the duration is per page.",
          },
          {
            type: "p",
            text: "Drag rows by their handle to change the order — items play top to bottom, then loop. Use Fit to choose how content fills the screen (Auto, Letterbox, Stretch or Crop). When you’re happy, click Save.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Around 8–12 seconds per image reads comfortably for most audiences. Click Preview playlist to watch the whole sequence before you save.",
          },
        ],
        screenshot: {
          src: "content/build-a-playlist/04.png",
          alt: "The playlist editor with per-item durations, drag handles and the Save button",
          caption: "Give each item a duration and drag to reorder.",
          todo: "Playlist editor with several items, Duration fields, drag handles, Fit selector, the Live Preview / Playlist Health / Playback Settings panels and the Save button. Use a generic playlist name (not a campaign/test name).",
        },
      },
      {
        title: "Set when a file plays (optional)",
        blocks: [
          {
            type: "p",
            text: "By default every file plays all the time. To show a file only for an event or a period, click the calendar button in its row to open Playback times.",
          },
          {
            type: "ul",
            items: [
              "Play only between: set when the file Starts and Ends (a date and a time). It plays non-stop between the two, for example from Thursday 10:00 to Friday 18:00. Leave Starts empty to begin now, or Ends empty to keep it playing.",
              "Only on certain days and times: tick it to add weekly rows, for example Monday to Friday from 08:00 to 09:00. Leave From and To empty to play all day on those days. Weekly rows apply inside the Starts and Ends dates.",
            ],
          },
          {
            type: "p",
            text: "Click Save. The row then shows when the file plays. Outside those times the file is skipped and the rest of the playlist keeps playing. Click Apply changes so the screens receive it: from then on each screen starts and stops the file by itself at the right time, even if it is offline at that moment.",
          },
          {
            type: "note",
            tone: "info",
            text: "Times are each screen's own local time. If a screen shows the wrong time, set its Time zone in Wall view (screen panel, Schedule section).",
          },
        ],
        screenshot: {
          src: "content/build-a-playlist/06.png",
          alt: "The Playback times dialog for one file: Play only between Thursday 10:00 and Friday 18:00, the weekly option, and Save",
          caption: "Playback times for one file: it plays from Thursday 10:00 to Friday 18:00.",
        },
      },
      {
        title: "Download the playlist to your PC (optional)",
        blocks: [
          {
            type: "p",
            text: "To keep a copy of everything a playlist plays — for a backup, or to hand the files to someone — click Download in the playlist editor, or the download icon on the playlist’s card on the Playlists page.",
          },
          {
            type: "p",
            text: "The Download playlist dialog shows how many files there are and their total size. Click Download ZIP and your browser saves the ZIP like any other download. It holds:",
          },
          {
            type: "ul",
            items: [
              "The original files (not the lighter copies screens play), numbered in play order — for example “01 - Breakfast Menu.png”. A file used twice is stored once.",
              "_contents.csv, which lists every item in play order with its duration, including anything that isn’t in the ZIP.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Apps play live, so they have no file to download — the dialog lists them under Not included. The ZIP holds the saved playlist: save your changes first if you want them included.",
          },
        ],
        screenshot: {
          src: "content/build-a-playlist/05.png",
          alt: "The Download playlist dialog over the playlist editor, showing the number of files, the total size and the Download ZIP button",
          caption: "Download every file in the playlist as one ZIP.",
          todo: "Playlist editor for 'Lunch Specials' with the Download playlist dialog open (4 files, total size, the ZIP name and _contents.csv note, Cancel / Download ZIP).",
        },
      },
      {
        title: "Show it on your website (optional)",
        blocks: [
          {
            type: "p",
            text: "Click Embed in the playlist editor to get a code you can paste into any web page. The page plays the playlist the way your screens do.",
          },
          {
            type: "steps-ref",
            label: "Show a playlist or channel on your website",
            to: "/content/embed-on-website",
          },
        ],
      },
      {
        title: "Assign it to a screen",
        blocks: [
          {
            type: "p",
            text: "Your playlist is ready. Put it on a screen from the screen’s Set content dialog (Playlists tab), or add it to a zone inside a channel. The editor’s Assigned To panel shows every channel and screen already using it.",
          },
          {
            type: "steps-ref",
            label: "Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "Upload media", to: "/content/upload-media" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Create a multi-zone channel", to: "/content/create-a-channel" },
      { label: "Show a playlist or channel on your website", to: "/content/embed-on-website" },
    ],
    keywords: ["playlist", "sequence", "loop", "order", "duration", "fit", "tags", "download", "zip", "backup", "export", "embed", "website", "schedule", "playback times", "start date", "end date", "expire"],
  },

  {
    slug: "create-a-channel",
    category: "content",
    route: "/content/create-a-channel",
    title: "Create a multi-zone channel",
    summary:
      "Split a screen into zones — for example a main video with a side panel and a footer ticker — and set its exact size.",
    intro:
      "A channel divides a screen into separate zones, each playing its own content at the same time. A common layout is a large main video, a side panel, and a scrolling footer ticker. This guide walks through creating a channel, choosing its layout, filling the zones, setting the channel size, and publishing it to screens.",
    prerequisites: [
      "You have media, playlists, or apps ready to place inside the zones.",
      "You are signed in with permission to manage content.",
    ],
    readingTime: 9,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open Channels",
        blocks: [
          {
            type: "p",
            text: "Click Channels in the sidebar. Each card shows the channel’s status — Draft (never published), Published, or Scheduled (published, with later edits that aren’t published yet) — with Edit, Preview and Duplicate buttons (plus Publish on a channel that isn’t fully published).",
          },
          {
            type: "p",
            text: "The ⋯ menu on a card has Rename, Duplicate, Download to PC and Delete. A channel that is playing on screens can’t be deleted; the dialog lists those screens so you can give them other content first.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/01.png",
          alt: "The Channels page with the sidebar Channels item highlighted",
          caption: "The Channels page — your multi-zone layouts.",
          todo: "Channels page (/channels) with generic channel names, summary cards and the '+ New Channel' button.",
        },
      },
      {
        title: "Create the channel",
        blocks: [
          {
            type: "p",
            text: "Click + New Channel. Type a name for the channel and click Continue. Then choose an orientation — Landscape or Portrait — and click Create Channel. The channel editor opens.",
          },
          {
            type: "note",
            tone: "info",
            text: "A channel plays only on screens with the same orientation, so pick the one that matches your screens.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/02.png",
          alt: "The Create a new channel dialog with the channel name field and Continue button",
          caption: "Name the channel, then pick its orientation.",
          todo: "Create a new channel dialog with a sample name typed and the centred Continue button.",
        },
      },
      {
        title: "Choose a layout",
        blocks: [
          {
            type: "p",
            text: "In the Preview & layout card on the right, click Edit Layout. The Choose Layout dialog lists Presets and My layouts. Pick one to see its zones, then click Use layout.",
          },
          {
            type: "p",
            text: "To draw your own, click Create custom layout. In the Layout Editor you can Split Horizontally or Vertically, add a Ticker Bar, Sidebar, Header or Footer, and drag zones to move or resize them. Click Save & Use when it’s ready.",
          },
          {
            type: "note",
            tone: "tip",
            text: "To add or remove a single zone later, use + Add layer / zone in the Zones & layers card. A channel can also hold several layouts that play one after another — click + Add layout.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/03.png",
          alt: "The Choose Layout dialog with preset layouts and the zones of the selected layout",
          caption: "Start from a preset or draw your own layout.",
          todo: "Channel editor with the Choose Layout dialog open on the Presets tab, a layout selected and its 'Zones in this layout' list, with Use layout / Preview layout / Edit zones buttons.",
        },
      },
      {
        title: "Add content to each zone",
        blocks: [
          {
            type: "p",
            text: "Pick a zone from the Zone menu in the toolbar (or click it in the preview), then click + Add Content. Choose from the Media, Playlists or Apps tab and click Add. Each zone keeps its own Content Queue — drag items to reorder them; they play top to bottom.",
          },
          {
            type: "p",
            text: "Select an item to see it in Schedule Studio, where you can limit when it plays — see the next step.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/04.png",
          alt: "The channel editor with the content queue, Schedule Studio and Preview & layout panels",
          caption: "Give every zone its own content.",
          todo: "Channel editor with a zone selected: Content Queue with a few items, Schedule Studio, and the Preview & layout / Zones & layers cards. Use a generic channel name and neutral preview images (no identifiable venue).",
        },
      },
      {
        title: "Schedule when an item plays (optional)",
        blocks: [
          {
            type: "p",
            text: "Select an item in the Content Queue. Schedule Studio lists its schedules; an item with no schedule is always available. To add one:",
          },
          {
            type: "ol",
            items: [
              "Click + Add schedule. The new schedule is marked Unsaved and doesn’t change anything until you add it.",
              "Choose the Recurrence: Every day, Weekdays (Mon – Fri), Weekends (Sat – Sun), Selected weekdays (then pick the Days), or Specific date range (then pick From and To).",
              "To limit the hours, turn off All day and set the Start and End of the Time range. An end before the start runs past midnight — for example 18:00 to 08:00 — and the studio notes “Crosses midnight”.",
              "Turn on Play in full screen if the item should take over the whole screen while this schedule is active.",
              "Click Add schedule (Cancel throws it away).",
            ],
          },
          {
            type: "p",
            text: "An item can have several schedules and plays whenever any of its active ones matches. To change a schedule, click it, edit it and click Save schedule. The switch at the top pauses a schedule without deleting it (it shows Paused), and the bin icon deletes it; delete them all and the item is always available again.",
          },
          {
            type: "ul",
            items: [
              "Two schedules of the same item can’t cover the same day and time: the studio says which one it overlaps and won’t save until you change the days or times.",
              "It also won’t save a schedule with no day picked, a missing or reversed date range, or the same start and end time.",
              "The selected item shows Eligible now, Not eligible now or All schedules paused. Weekly availability, below the editor, charts its schedules (paused ones are dimmed); switch it to All channel content to see every item in the zone.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Screens follow exactly what the studio shows: a paused schedule never plays, a date range plays on every day inside it, and overnight times play across midnight. The studio shows times in your workspace’s time zone; each screen applies them in its own time zone. Schedule changes are part of the draft — publish the channel to send them to screens.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/06.png",
          alt: "Schedule Studio with two weekday schedules for one item — a daytime one and an overnight one with Play in full screen on — and the weekly availability chart",
          caption: "Give an item one or more schedules; the chart shows when it plays.",
          todo: "Channel editor (Reception Display) at 1600×1000, first Main Zone item selected: Schedules list with a 08:00–18:00 weekday schedule and an 18:00–08:00 weekday schedule (selected, Crosses midnight hint, Play in full screen on), Weekly availability below.",
        },
      },
      {
        title: "Set the channel size",
        blocks: [
          {
            type: "p",
            text: "The Preview & layout card shows the channel size, for example 1920 × 1080 (16:9). Click Change to open the Channel Size dialog:",
          },
          {
            type: "ul",
            items: [
              "Pick a preset — Full HD, UHD 4K, Portrait Full HD, Portrait 4K, Stretched bar / 2×1 wall (3840 × 1080) and more.",
              "Or choose Custom size… and type the Width (px) and Height (px). The swap button flips them.",
              "Under Match a screen on this channel, click a screen to use the exact size it displays, so the channel fills it 1:1.",
            ],
          },
          {
            type: "p",
            text: "Click Confirm. If a screen on the channel has a different shape, a hint under the preview names it, with a Match button. Use Display fit to choose how the channel scales on screens: Auto (screen decides), Fit (letterbox), Stretch (fill) or Fill (crop).",
          },
          {
            type: "note",
            tone: "info",
            text: "For a video wall, set the channel to the whole wall’s size — for example 3840 × 1080 for two Full HD screens side by side. The wall plays the channel at that size, so each screen shows its part at full sharpness.",
          },
          {
            type: "note",
            tone: "warning",
            text: "Changing the size affects screens already playing the channel. Zones keep their positions in percent, so they stretch to the new shape. If the new size flips the orientation, the channel changes orientation when you publish.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/05.png",
          alt: "The Channel Size dialog with presets, Custom size fields and the Match a screen list",
          caption: "Match the channel to your screens’ exact size.",
          todo: "Channel editor with the Channel Size dialog open on 'Custom size…': Width (px) / Height (px) fields, the aspect readout, and the 'Match a screen on this channel' list.",
        },
      },
      {
        title: "Add background music (optional)",
        blocks: [
          {
            type: "p",
            text: "Choose Background Audio from the Zone menu, then click + Add Content and add audio files. They form a Music Queue that loops across the whole channel, whatever the zones are showing.",
          },
          {
            type: "p",
            text: "In Background Music Studio, set the Master volume and each track’s volume, and turn on Mute background music when channel audio is active (or Duck instead of mute) so the music gets out of the way when a video with sound plays. Click ▶ Test output to hear the selected track.",
          },
          {
            type: "p",
            text: "The channel preview (Preview draft) plays the music too, by the same rules a screen uses: it loops at the volumes you set, gives way while a video with sound is on screen, comes back when the video ends, and stops when you close the preview.",
          },
          {
            type: "note",
            tone: "info",
            text: "Channel audio, under Channel settings, is the master switch for sound. When it’s off, nothing plays — no video sound and no background music — on screens and in the preview, and Test output is unavailable, with a note saying to turn Channel audio on first.",
          },
        ],
      },
      {
        title: "Save, publish, and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Click Save draft to keep your work, and Preview draft to watch it. The preview plays the channel the way a screen would at that moment — with the background music, and with full screen only for items whose active schedule says so. Screens only change when you publish: click Publish and confirm with Publish Channel. After later edits, the button reads Publish draft — screens keep the published version until you publish again.",
          },
          {
            type: "p",
            text: "Then put the channel on a screen from the screen’s Set content dialog (Channels tab), just like any other content.",
          },
          {
            type: "steps-ref",
            label: "Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
      {
        title: "Download, embed, duplicate or delete the channel",
        blocks: [
          {
            type: "p",
            text: "Click the … button at the top right of the channel editor:",
          },
          {
            type: "ul",
            items: [
              "Download to PC — every media file the channel plays, as one ZIP, with a folder per zone (and per layout when there are several), the background music, and _contents.csv listing every item. For a published channel, choose Published (what screens are playing) or Draft (what the editor shows) first. Apps play live, so they aren’t included.",
              "Embed on website — a code you paste into any web page, which plays the published channel the way your screens do. See Show a playlist or channel on your website.",
              "Duplicate — saves your unsaved changes, then opens a copy named “<name> (copy)” (then “(copy 2)” and so on). The copy keeps the channel’s size and display fit.",
              "Delete — asks you to confirm, then returns to Channels. A channel that is playing on screens can’t be deleted: the dialog lists those screens and the button reads In use until you assign other content to them.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "The same Duplicate, Download to PC and Delete items are in the ⋯ menu on each card on the Channels page.",
          },
        ],
        screenshot: {
          src: "content/create-a-channel/07.png",
          alt: "The channel editor’s … menu open with Download to PC, Duplicate and Delete",
          caption: "Download, duplicate or delete from the editor’s … menu.",
          todo: "Channel editor (Reception Display) at 1600×1000 with the … menu at the top right open: Download to PC, Duplicate, Delete.",
        },
      },
    ],
    related: [
      { label: "Build a playlist", to: "/content/build-a-playlist" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Upload media", to: "/content/upload-media" },
      { label: "Show a playlist or channel on your website", to: "/content/embed-on-website" },
    ],
    keywords: [
      "channel",
      "zones",
      "layout",
      "regions",
      "ticker",
      "channel size",
      "custom size",
      "resolution",
      "video wall",
      "stretched",
      "display fit",
      "background music",
      "audio",
      "schedule",
      "schedule studio",
      "full screen",
      "overnight",
      "pause schedule",
      "publish",
      "duplicate",
      "download",
      "zip",
      "embed",
      "website",
    ],
  },

  {
    slug: "embed-on-website",
    category: "content",
    route: "/content/embed-on-website",
    title: "Show a playlist or channel on your website",
    summary:
      "Copy a ready-made code and paste it into your website: the page plays your playlist or channel the way your screens do.",
    intro:
      "An embed plays a playlist or channel inside any web page — your company site, an intranet, a booking page. You create a link in the playlist or channel editor, copy the code it gives you, and paste it into your website. Visitors can only watch: the link can’t change anything in PulsePanels, and it shows nothing but the content you chose.",
    prerequisites: [
      "You are signed in with permission to put content on screens.",
      "For a channel: it has been published at least once.",
      "Access to edit your website, or someone who can paste the code for you.",
    ],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-10-11",
    steps: [
      {
        title: "Open the playlist or channel",
        blocks: [
          {
            type: "p",
            text: "Open the playlist or channel you want on your website. In the playlist editor, click Embed at the top. In the channel editor, click the … button at the top right and choose Embed on website.",
          },
          {
            type: "note",
            tone: "info",
            text: "Don’t see the button? It shows for people who can put content on screens. Ask an administrator of your account if you need it.",
          },
        ],
        screenshot: {
          src: "content/embed-on-website/01.png",
          alt: "The playlist editor with the Embed button at the top, next to Download",
          caption: "Embed sits next to Download in the playlist editor.",
          todo: "Playlist editor for 'Lunch Specials' at 1600×1000, header buttons visible: Preview playlist, Download, Embed, Save.",
        },
      },
      {
        title: "Create an embed link",
        blocks: [
          {
            type: "p",
            text: "In the Embed on website dialog, type where the content will be shown — for example “Company website” — so you can tell your links apart later. Then click Create embed link.",
          },
          {
            type: "p",
            text: "The dialog shows a live preview of what your visitors will see, the embed code and a direct link. You can create more than one link for the same playlist or channel, for example one per website.",
          },
        ],
        screenshot: {
          src: "content/embed-on-website/02.png",
          alt: "The Embed on website dialog with a live preview of the playlist, the embed code and the Copy code button",
          caption: "Each link comes with a live preview and a ready-made code.",
          todo: "Embed on website dialog for 'Lunch Specials' with one link named 'Company website': preview playing, embed code, Copy code / Copy link, Update / Open.",
        },
      },
      {
        title: "Paste the code into your website",
        blocks: [
          {
            type: "p",
            text: "Click Copy code and paste it where the content should appear on your website. Most website builders have a place for this:",
          },
          {
            type: "ul",
            items: [
              "WordPress — add a Custom HTML block.",
              "Wix — add an Embed code (Embed HTML) element.",
              "Squarespace — add a Code block.",
              "Your own site — paste it into the page’s HTML.",
            ],
          },
          {
            type: "p",
            text: "The code makes the player as wide as the space it sits in, with the shape of your playlist or channel (for example 16:9 for landscape), so nothing is cut off. If you’d rather share a link than embed it, use Copy link — it opens the content on its own page.",
          },
          {
            type: "note",
            tone: "info",
            text: "Videos play muted on websites, because browsers don’t allow pages to start sound on their own.",
          },
        ],
      },
      {
        title: "Keep it up to date",
        blocks: [
          {
            type: "p",
            text: "Your website shows the playlist or channel as it was last applied — exactly like a screen. After you edit it, click Apply changes at the top of the page (the website is updated with your screens), or click Update in the Embed on website dialog to update just the website.",
          },
          {
            type: "p",
            text: "Open the dialog any time to copy the code again. The date under the link’s name shows when its content was last updated.",
          },
        ],
      },
      {
        title: "Replace or remove a link",
        blocks: [
          {
            type: "ul",
            items: [
              "Make a new link — gives the link a new address. The old code stops playing straight away, so paste the new code into your website.",
              "Delete — removes the link. Every website that uses it shows a blank area until you remove the code.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Shared a link by mistake, or someone copied your code to their own site? Make a new link: only the new code plays.",
          },
          {
            type: "p",
            text: "Each link is listed in the editor’s Assigned To panel as “Website embed · <name>”. Like a screen, it keeps the playlist or channel in use: delete its embed links before you can delete the playlist or channel itself.",
          },
        ],
      },
      {
        title: "What visitors see",
        blocks: [
          {
            type: "p",
            text: "An embed plays every item in the playlist or channel, in a box the size your website gives it. It is not a screen: it doesn’t use a screen licence and doesn’t appear on your Screens page. Some things stay on your screens only:",
          },
          {
            type: "ul",
            items: [
              "Emergency alerts and Smart Ads campaigns.",
              "A Queue Kiosk can’t print tickets from a website.",
              "Meeting room displays can’t book a room from a website.",
              "Patient lists (Clinic Flow) and staff arrivals (Staff Welcome) aren’t shown.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Build a playlist", to: "/content/build-a-playlist" },
      { label: "Create a multi-zone channel", to: "/content/create-a-channel" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: ["embed", "website", "web page", "iframe", "wordpress", "wix", "squarespace", "share", "link", "intranet", "html", "code"],
  },

  {
    slug: "design-in-canvas",
    category: "content",
    route: "/content/design-in-canvas",
    title: "Design content in Canvas",
    summary:
      "Start from a template or a blank canvas and design your own signage with the drag-and-drop editor.",
    intro:
      "Canvas is a drag-and-drop editor for building your own signage without any design software. Start from one of the ready-made templates or a blank canvas, add text, images and shapes, then put the finished design on a screen, in a playlist, or save it to your Media Library as an image.",
    prerequisites: [
      "Any images you want to use are in your Media Library.",
      "You are signed in with permission to manage content.",
    ],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Canvas",
        blocks: [
          {
            type: "p",
            text: "Click Canvas in the sidebar. The page has three tabs: Template Gallery (ready-made designs, grouped by industry such as Healthcare, Education and Retail), My Canvases (your saved designs) and My Templates.",
          },
        ],
        screenshot: {
          src: "content/design-in-canvas/01.png",
          alt: "The Canvas page with the Template Gallery tab open",
          caption: "Start from a template or a blank canvas.",
          todo: "Canvas page (/canvas) on the Template Gallery tab with category chips and template cards, and the New Canvas button. Retail chip selected (the all-templates view shows stock photos of people).",
        },
      },
      {
        title: "Start a new design",
        blocks: [
          {
            type: "p",
            text: "To start from scratch, click New Canvas. In Set your Canvas dimensions, choose Landscape (1920 × 1080) for most TVs or Portrait (1080 × 1920) for upright displays, then click Create Canvas.",
          },
          {
            type: "p",
            text: "To start from a template instead, click it in the Template Gallery to preview it, then click Use Template → (or choose Use Template from the card’s ··· menu). You get your own copy to edit; the original template doesn’t change.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Match the orientation to the screen you’re designing for so nothing is cropped or letterboxed.",
          },
        ],
        screenshot: {
          src: "content/design-in-canvas/02.png",
          alt: "The Set your Canvas dimensions dialog with Landscape and Portrait options",
          caption: "Pick Landscape or Portrait to match your screen.",
          todo: "'Set your Canvas dimensions' dialog with Landscape / Portrait cards and the Create Canvas button.",
        },
      },
      {
        title: "Add elements",
        blocks: [
          {
            type: "p",
            text: "Pick a tool from the toolbar on the left — Text, Image, Rectangle, Circle, Triangle or Line — then click the canvas to add it. The Image tool opens your Media Library so you can choose a picture. Use Select to move elements and drag their handles to resize.",
          },
          {
            type: "p",
            text: "A design can have several slides that play one after another: click Add slide, and set each slide’s seconds on screen. Import file turns the pages of a PDF, PowerPoint or image into editable slides.",
          },
        ],
        screenshot: {
          src: "content/design-in-canvas/03.png",
          alt: "The Canvas editor with the tool bar on the left and elements on the artboard",
          caption: "Pick a tool, then click the canvas to add it.",
          todo: "Canvas editor (open a design) with text, an image and a shape on the artboard, the left toolbar (Select, Text, Image, Rectangle, Circle, Triangle, Line) and the slide strip with 'Add slide' / 'Import file'.",
        },
      },
      {
        title: "Style your elements",
        blocks: [
          {
            type: "p",
            text: "Select an element to open its properties in the Element tab on the right — font, font size, text colour, background, alignment, size, rotation and opacity. For images you can crop to a shape and reposition or zoom the photo inside its frame. The Layers tab lists every element so you can reorder or delete them.",
          },
          {
            type: "p",
            text: "Undo and redo are at the top (Ctrl+Z / Ctrl+Shift+Z). On a template, Reset takes the design back to the original.",
          },
        ],
        screenshot: {
          src: "content/design-in-canvas/04.png",
          alt: "The Element properties panel for a selected text element in Canvas",
          caption: "Fine-tune fonts, colours and sizes per element.",
          todo: "Canvas editor with a text element selected and the Element tab open on the right (Font, Font Size, Text Color, Alignment & Style, Position & Size).",
        },
      },
      {
        title: "Save and use it on a screen",
        blocks: [
          {
            type: "p",
            text: "Click Save Design. The design appears under My Canvases, and you can then:",
          },
          {
            type: "ul",
            items: [
              "Click Set to Screen and choose a screen to publish it to straight away.",
              "Add it to a playlist from the Canvas tab of the Media Picker, or pick it from the Canvas tab when you set a screen’s content.",
              "Click Save to Library to export it as an image into your Media Library.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Screens showing a design update when you save your changes again — you don’t need to reassign it.",
          },
          {
            type: "steps-ref",
            label: "Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "Upload media", to: "/content/upload-media" },
      { label: "Build a playlist", to: "/content/build-a-playlist" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: [
      "canvas",
      "design",
      "editor",
      "drag and drop",
      "template",
      "slides",
      "import pdf",
      "powerpoint",
    ],
  },
];
